import { measurePaintedFrame } from './previewFrameGeometry';
import { buildLinedGradient } from './linedOverlayStyle';
import { CANVAS_ELEMENT_HIGHLIGHT } from '../PreviewDisplayConstants';

// The four squares a picture is resized by. The signs say which way the corner sits from the middle
// of the picture, which is both where the square is pinned and the direction a drag on it grows the
// picture in.
const RESIZE_HANDLES = [
    { name: 'top-left', signX: -1, signY: -1, cursor: 'nwse-resize' },
    { name: 'top-right', signX: 1, signY: -1, cursor: 'nesw-resize' },
    { name: 'bottom-right', signX: 1, signY: 1, cursor: 'nwse-resize' },
    { name: 'bottom-left', signX: -1, signY: 1, cursor: 'nesw-resize' }
];

// Shows which element the pointer is on, or which one is being dragged, and carries the four corner
// squares that resize it. This is editor chrome and deliberately lives in the DOM above the canvas
// rather than in the PIXI stage: the stage is also what getExtractedCanvas() rasterises into the
// saved video thumbnail, and an outline baked into a user's thumbnail is not something they can undo.
//
// The squares are shown only for a target that can be resized, which is an image element and nothing
// else: text and the visualizer are dragged, and their size stays with the settings panel.
export default class CanvasElementHighlight {

    constructor(container, canvas) {
        this.container = container;
        this.canvas = canvas;

        // The target the outline is currently drawn around, so whoever grabs a corner square knows
        // which element it belongs to.
        this.target = null;

        // While a corner is being dragged the outline belongs to that drag alone: a stray hover
        // update must not move it onto an element the pointer happens to pass over.
        this.lockedTarget = null;

        this.element = document.createElement('div');
        this.element.setAttribute('aria-hidden', 'true');
        this.element.setAttribute('data-canvas-element-highlight', 'true');

        Object.assign(this.element.style, {
            position: 'absolute',
            display: 'none',
            boxSizing: 'border-box',
            // The pointer belongs to the canvas underneath, which is what runs the hit testing and
            // the move drag. Only the corner squares take it back, and they do so on themselves.
            pointerEvents: 'none',
            zIndex: 2,
            ...buildLinedBorderStyle()
        });

        this.handles = RESIZE_HANDLES.map(corner => this.createHandle(corner));

        // A pointer that reaches a corner square has left the canvas, and the canvas will therefore
        // never report it leaving the element afterwards. Without this the outline would be left
        // behind on screen whenever the user went from a square straight to the settings panel.
        this.element.addEventListener('pointerleave', this.handlePointerLeave);

        this.container.appendChild(this.element);
    }

    handlePointerLeave = (event) => {
        // Back onto the canvas, or across to another square, is not leaving: the canvas takes the
        // hover over again in the first case and the outline is still wanted in the second.
        if (event.relatedTarget === this.canvas || this.containsElement(event.relatedTarget))
            return;

        this.hide();
    };

    createHandle(corner) {
        const element = document.createElement('div');
        element.setAttribute('data-canvas-element-resize-handle', corner.name);

        Object.assign(element.style, {
            position: 'absolute',
            boxSizing: 'border-box',
            width: `${CANVAS_ELEMENT_HIGHLIGHT.HANDLE_SIZE}px`,
            height: `${CANVAS_ELEMENT_HIGHLIGHT.HANDLE_SIZE}px`,
            // Pinned to the corner of the outline and pulled back by half its own size, so the square
            // straddles the corner instead of hanging off one side of it.
            left: corner.signX < 0 ? '0' : '100%',
            top: corner.signY < 0 ? '0' : '100%',
            transform: 'translate(-50%, -50%)',
            background: CANVAS_ELEMENT_HIGHLIGHT.LINE_COLOR,
            border: `${CANVAS_ELEMENT_HIGHLIGHT.HANDLE_BORDER_THICKNESS}px solid ${CANVAS_ELEMENT_HIGHLIGHT.ALTERNATE_LINE_COLOR}`,
            borderRadius: '2px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.45)',
            cursor: corner.cursor,
            // The outline hands the pointer through to the canvas; the squares are the exception,
            // because a drag on one of them is a resize and not a move.
            pointerEvents: 'auto',
            // Without this a touch drag on a square scrolls the page instead of resizing.
            touchAction: 'none'
        });

        this.element.appendChild(element);

        return { element: element, corner: corner };
    }

    getHandles() {
        return this.handles;
    }

    getTarget() {
        return this.target;
    }

    // True for the outline itself and for anything it carries, which is how the canvas tells a
    // pointer that left it for a corner square from one that left it for good.
    containsElement(element) {
        return Boolean(element) && this.element.contains(element);
    }

    // Holds the outline on one element for the length of a corner drag. Released on pointer up.
    lockTo(target) {
        this.lockedTarget = target;
    }

    unlock() {
        this.lockedTarget = null;
    }

    // Targets are rebuilt on every pointer event, so they are compared by the identity they carry
    // rather than by reference: the same picture arrives as a different object each time.
    showAround(target) {
        if (this.lockedTarget && target.id !== this.lockedTarget.id)
            return;

        const bounds = target.getBounds();

        if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
            this.hide();
            return;
        }

        const frame = measurePaintedFrame(this.canvas);
        const containerBounds = this.container.getBoundingClientRect();

        this.target = target;
        this.element.setAttribute('data-canvas-element-highlight-type', target.type);
        this.showHandles(target.canResize);
        this.element.style.left = `${frame.left - containerBounds.left + bounds.x * frame.scale}px`;
        this.element.style.top = `${frame.top - containerBounds.top + bounds.y * frame.scale}px`;
        this.element.style.width = `${bounds.width * frame.scale}px`;
        this.element.style.height = `${bounds.height * frame.scale}px`;
        this.element.style.display = 'block';
    }

    // Hidden rather than taken off the outline, because the resize controller listens on the squares
    // themselves and would have to be rebuilt every time the pointer crossed from a picture to a line
    // of text. A hidden element takes no pointer events, so a drag on one is impossible either way.
    showHandles(visible) {
        this.handles.forEach(({ element }) => {
            element.style.display = visible ? 'block' : 'none';
        });
    }

    hide() {
        if (this.lockedTarget)
            return;

        this.target = null;

        if (this.element.style.display !== 'none')
            this.element.style.display = 'none';
    }

    destroy() {
        this.target = null;
        this.lockedTarget = null;
        this.element.removeEventListener('pointerleave', this.handlePointerLeave);
        this.element.remove();
    }
}

// The red and white lined outline, drawn as four striped strips rather than a border. Why striped
// rather than dashed is explained where the gradient itself is built.
function buildLinedBorderStyle() {
    const { LINE_THICKNESS } = CANVAS_ELEMENT_HIGHLIGHT;
    const horizontal = buildLinedGradient('90deg');
    const vertical = buildLinedGradient('180deg');

    return {
        // Top, bottom, left, right.
        backgroundImage: `${horizontal}, ${horizontal}, ${vertical}, ${vertical}`,
        backgroundSize: `100% ${LINE_THICKNESS}px, 100% ${LINE_THICKNESS}px, ${LINE_THICKNESS}px 100%, ${LINE_THICKNESS}px 100%`,
        backgroundPosition: '0 0, 0 100%, 0 0, 100% 0',
        backgroundRepeat: 'no-repeat'
    };
}
